import { forwardRef, type Ref } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import snippsnappLogo from "@/assets/snippsnapp-logo.png";

interface LogoProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  mobileSize?: "xs" | "sm" | "md" | "lg" | "xl";
  linkTo?: string;
  className?: string;
}

const Logo = forwardRef<HTMLAnchorElement | HTMLDivElement, LogoProps>(
  ({ size = "md", mobileSize, linkTo = "/", className }, ref) => {
    const sizeConfigs = {
      xs: { class: "h-7 sm:h-8", height: 28, width: 112 },
      sm: { class: "h-8 sm:h-10", height: 32, width: 128 },
      md: { class: "h-11 sm:h-[52px]", height: 44, width: 176 },
      lg: { class: "h-14 sm:h-[68px]", height: 56, width: 224 },
      xl: { class: "h-20 sm:h-24", height: 80, width: 320 },
    };

    const config = sizeConfigs[size];
    const mobileConfig = mobileSize ? sizeConfigs[mobileSize] : null;

    // If mobileSize is provided, use responsive classes
    const logoHeight = mobileConfig 
      ? `${mobileConfig.class} sm:${config.class}`
      : config.class;

    const content = (
      <img 
        src={snippsnappLogo} 
        alt="SnippSnapp - Personliga barnböcker"
        className={cn("w-auto", logoHeight)}
        width={config.width}
        height={config.height}
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
    );

    if (linkTo) {
      return (
        <Link to={linkTo} className={cn("flex items-center group", className)} ref={ref as Ref<HTMLAnchorElement>}>
          {content}
        </Link>
      );
    }

    return (
      <div ref={ref as Ref<HTMLDivElement>} className={cn("flex items-center", className)}>
        {content}
      </div>
    );
  }
);

Logo.displayName = "Logo";

export default Logo;
